import { useQuery } from '@tanstack/react-query';
import {
  type BudgetKind,
  formatMoney,
  type JobBudgetResponse,
  type JobResponse,
  type MonthlyFlows,
  SNAPSHOT_SOURCE_LABELS,
  sumFlows,
  totalOf,
} from '@k-management/shared';
import { useState } from 'react';
import { apiRequest } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { jobKeys } from './jobs-ui.js';

type View = 'previsto' | 'consuntivo' | 'confronto';

const LABELS: Record<
  BudgetKind,
  { in: string; out: string; net: string; planned: string; explain: string }
> = {
  economico: {
    in: 'Ricavi',
    out: 'Costi',
    net: 'Margine',
    planned: 'previsti',
    explain:
      'Imponibile, senza IVA, per data del documento: emissione dei ricavi, ricevimento dei costi.',
  },
  tesoreria: {
    in: 'Entrate',
    out: 'Uscite',
    net: 'Saldo',
    planned: 'previste',
    explain:
      'Le somme che entrano ed escono davvero: senza l’IVA in split payment, al netto delle ritenute; un anticipo della banca conta quando è accreditato. Per data di incasso e di pagamento.',
  },
};

const MONTHS = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const monthLabel = (key: string) => `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}`;

/** The value of a month in some flows, zero when the month is not there. */
function at(flows: MonthlyFlows, side: 'in' | 'out', month: string): number {
  const index = flows.months.indexOf(month);
  return index < 0 ? 0 : Number(flows[side][index]);
}

/**
 * The budget of a commessa (2.05, decisions of 17/10/2026): economico or
 * tesoreria; previsto, consuntivo or the two side by side; the iniziale of
 * the base offer or of the «Avvia» against the attuale.
 */
export function BudgetView({ job }: { job: JobResponse }) {
  const [kind, setKind] = useState<BudgetKind>('economico');
  const [view, setView] = useState<View>('previsto');
  const budget = useQuery({
    queryKey: [...jobKeys.all, 'budget', job.id],
    queryFn: () => apiRequest<JobBudgetResponse>(`/jobs/${job.id}/budget`),
  });
  if (budget.isError) return <p className="error">{budget.error.message}</p>;
  if (!budget.data) return <p className="muted">Caricamento…</p>;
  const data = budget.data;
  const labels = LABELS[kind];
  const plan = data.previsto[kind];
  const done = data.consuntivo[kind];
  const initial = data.initial?.[kind] ?? null;
  const months = sumFlows([plan, done]).months;
  const shown = view === 'consuntivo' ? done : plan;
  const net = (flows: MonthlyFlows) => Number(totalOf(flows.in)) - Number(totalOf(flows.out));

  return (
    <section className="stack">
      <div className="row between">
        <div className="segmented-small" role="group" aria-label="Budget">
          {(['economico', 'tesoreria'] as const).map((item) => (
            <button
              key={item}
              type="button"
              className={kind === item ? 'selected' : ''}
              onClick={() => setKind(item)}
            >
              {item === 'economico' ? 'Economico' : 'Tesoreria'}
            </button>
          ))}
        </div>
        <div className="segmented-small" role="group" aria-label="Vista">
          {(['previsto', 'consuntivo', 'confronto'] as const).map((item) => (
            <button
              key={item}
              type="button"
              className={view === item ? 'selected' : ''}
              onClick={() => setView(item)}
            >
              {item === 'previsto'
                ? 'Previsto'
                : item === 'consuntivo'
                  ? 'Consuntivo'
                  : 'Confronto'}
            </button>
          ))}
        </div>
      </div>
      <p className="muted small">{labels.explain}</p>
      {data.sumOf > 0 && <p className="notice info">Somma delle {data.sumOf} sottocommesse.</p>}

      <section className="card stack">
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th />
                <th className="right">
                  Iniziale
                  {data.initial && (
                    <div className="muted small">
                      {SNAPSHOT_SOURCE_LABELS[data.initial.source]}
                      {data.initial.quoteLabel && ` ${data.initial.quoteLabel}`},{' '}
                      {formatTimestamp(data.initial.takenAt)}
                    </div>
                  )}
                </th>
                <th className="right">Attuale</th>
                <th className="right">Differenza</th>
                <th className="right">
                  Consuntivo
                  {data.closed && (
                    <div className="muted small">
                      fermo alla chiusura del {formatTimestamp(data.closed.takenAt)}
                    </div>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  [labels.in, (flows: MonthlyFlows) => Number(totalOf(flows.in)), 1],
                  [labels.out, (flows: MonthlyFlows) => Number(totalOf(flows.out)), -1],
                  [labels.net, net, 1],
                ] as const
              ).map(([label, value, good], index) => {
                const start = initial ? value(initial) : null;
                const now = value(plan);
                const delta = start === null ? null : now - start;
                return (
                  <tr key={label} className={index === 2 ? 'total-row' : undefined}>
                    <td>{index === 2 ? <strong>{label}</strong> : label}</td>
                    <td className="right">{start === null ? '—' : formatMoney(start)}</td>
                    <td className="right">{formatMoney(now)}</td>
                    <td className={`right ${delta && delta * good < 0 ? 'negative' : ''}`}>
                      {delta === null ? '—' : `${delta > 0 ? '+' : ''}${formatMoney(delta)}`}
                    </td>
                    <td className="right">{formatMoney(value(done))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="muted small">
          {data.initial
            ? '«Iniziale» è la fotografia fatta all’origine e non cambia più; «Attuale» segue rate, costi e fatture; il consuntivo viene solo da fatture e banca.'
            : `L’«Iniziale» si fotografa all’avvio ${job.kind === 'progetto' ? 'del progetto' : 'della commessa'}.`}
        </p>
      </section>

      <section className="card stack">
        <BudgetChart
          kind={kind}
          view={view}
          months={months}
          plan={plan}
          done={done}
          labels={labels}
        />
        <div className="table-scroll">
          <table className="lines">
            <thead>
              {view === 'confronto' ? (
                <tr>
                  <th>Mese</th>
                  <th className="right">
                    {labels.in} {labels.planned}
                  </th>
                  <th className="right">{labels.in} a consuntivo</th>
                  <th className="right">
                    {labels.out} {labels.planned}
                  </th>
                  <th className="right">{labels.out} a consuntivo</th>
                </tr>
              ) : (
                <tr>
                  <th>Mese</th>
                  <th className="right">{labels.in}</th>
                  <th className="right">{labels.out}</th>
                  <th className="right">{labels.net}</th>
                  {kind === 'tesoreria' && <th className="right">Progressivo</th>}
                </tr>
              )}
            </thead>
            <tbody>
              {(() => {
                let running = 0;
                return months.map((month) => {
                  if (view === 'confronto') {
                    return (
                      <tr key={month}>
                        <td>{monthLabel(month)}</td>
                        <td className="right">{formatMoney(at(plan, 'in', month))}</td>
                        <td className="right">{formatMoney(at(done, 'in', month))}</td>
                        <td className="right">{formatMoney(at(plan, 'out', month))}</td>
                        <td className="right">{formatMoney(at(done, 'out', month))}</td>
                      </tr>
                    );
                  }
                  const value = at(shown, 'in', month) - at(shown, 'out', month);
                  running += value;
                  return (
                    <tr key={month}>
                      <td>{monthLabel(month)}</td>
                      <td className="right">{formatMoney(at(shown, 'in', month))}</td>
                      <td className="right">{formatMoney(at(shown, 'out', month))}</td>
                      <td className={`right ${value < 0 ? 'negative' : ''}`}>
                        {formatMoney(value)}
                      </td>
                      {kind === 'tesoreria' && (
                        <td className={`right ${running < 0 ? 'negative' : ''}`}>
                          {formatMoney(running)}
                        </td>
                      )}
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
        {months.length === 0 && (
          <p className="muted">
            {job.kind === 'progetto'
              ? 'Ancora niente: i costi e le fatture del progetto riempiono il budget.'
              : 'Ancora niente: rate, costi e fatture della commessa riempiono il budget.'}
          </p>
        )}
      </section>
    </section>
  );
}

/**
 * Bars by month (in and out side by side), the previsto light and the
 * consuntivo full in the comparison; for the tesoreria, the line of the running
 * balance. One axis; every bar says its value on hover.
 */
function BudgetChart({
  kind,
  view,
  months,
  plan,
  done,
  labels,
}: {
  kind: BudgetKind;
  view: View;
  months: string[];
  plan: MonthlyFlows;
  done: MonthlyFlows;
  labels: { in: string; out: string; net: string };
}) {
  if (months.length === 0) return null;
  const series = view === 'previsto' ? [plan] : view === 'consuntivo' ? [done] : [plan, done];
  const main = view === 'consuntivo' ? done : plan;
  const running: number[] = [];
  months.forEach((month, index) => {
    running.push((running[index - 1] ?? 0) + at(main, 'in', month) - at(main, 'out', month));
  });
  const values = [
    ...series.flatMap((flows) =>
      months.flatMap((month) => [at(flows, 'in', month), at(flows, 'out', month)]),
    ),
    ...(kind === 'tesoreria' ? running.map(Math.abs) : []),
  ];
  const max = Math.max(...values, 1);
  const width = 760;
  const height = 220;
  const top = 12;
  const base = kind === 'tesoreria' && running.some((value) => value < 0) ? 170 : 190;
  const slot = (width - 40) / months.length;
  const bar = Math.min(28, (slot - 14) / 2);
  const y = (value: number) => base - (value / max) * (base - top);

  return (
    <div className="budget-chart">
      <div className="budget-legend small">
        <span>
          <i className="swatch in" /> {labels.in}
        </span>
        <span>
          <i className="swatch out" /> {labels.out}
        </span>
        {kind === 'tesoreria' && (
          <span>
            <i className="swatch net" /> Saldo progressivo
          </span>
        )}
        {view === 'confronto' && <span className="muted">chiaro previsto · pieno consuntivo</span>}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Budget ${kind} per mese`}>
        <line x1={30} x2={width} y1={base} y2={base} className="axis" />
        {months.map((month, index) => {
          const x = 40 + index * slot + (slot - bar * 2 - 4) / 2;
          return (
            <g key={month}>
              {series.map((flows, layer) => {
                const planned = view === 'confronto' && layer === 0;
                const inset = view === 'confronto' && layer === 1 ? bar * 0.25 : 0;
                const thin = view === 'confronto' && layer === 1 ? bar * 0.5 : bar;
                return (['in', 'out'] as const).map((side, offset) => {
                  const value = at(flows, side, month);
                  const h = (value / max) * (base - top);
                  return (
                    <rect
                      key={`${layer}-${side}`}
                      x={x + offset * (bar + 4) + inset}
                      y={base - h}
                      width={thin}
                      height={Math.max(h, 0)}
                      rx={3}
                      className={`${side}${planned ? ' planned' : ''}`}
                    >
                      <title>
                        {`${side === 'in' ? labels.in : labels.out} ${monthLabel(month)}${
                          view === 'confronto' ? (planned ? ' previsti' : ' a consuntivo') : ''
                        }: ${formatMoney(value)}`}
                      </title>
                    </rect>
                  );
                });
              })}
              <text
                x={40 + index * slot + slot / 2}
                y={base + 18}
                textAnchor="middle"
                className="tick"
              >
                {monthLabel(month)}
              </text>
            </g>
          );
        })}
        {kind === 'tesoreria' && (
          <>
            <polyline
              className="net"
              points={running
                .map((value, index) => `${40 + index * slot + slot / 2},${y(value)}`)
                .join(' ')}
            />
            {running.map((value, index) => (
              <circle
                key={months[index]}
                cx={40 + index * slot + slot / 2}
                cy={y(value)}
                r={4}
                className="net-dot"
              >
                <title>{`Saldo a fine ${monthLabel(months[index]!)}: ${formatMoney(value)}`}</title>
              </circle>
            ))}
          </>
        )}
      </svg>
    </div>
  );
}
